// Tela de detalhe de um negocio (CRM + propostas + atividades + interacoes).
// Onda 1 (2026-06-02): migrado pra Antd — header com valor/status destacados,
// Steps interativo substituindo funil chevron custom, Cards Antd na sidebar
// (so renderiza painel quando tem dado), PDFs Ploomes como Dropdown no header
// (antes ficavam escondidos no fim da sidebar). As 8 abas internas (Timeline,
// Propostas, Composicoes, Vendas, Atividades, Anexos, Mapa, Edit) ainda usam
// inline-styles + sub-componentes legacy — Onda 2 cobre isso.

import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Tabs, App as AntApp, Button, Card, Descriptions, Tag, Statistic,
  Dropdown, Typography, Space, Row, Col, Spin, Result, Tooltip, DatePicker,
} from 'antd';
import dayjs from 'dayjs';
import {
  ArrowLeftOutlined, CheckOutlined, CloseOutlined, EditOutlined,
  FilePdfOutlined, MailOutlined, PhoneOutlined, EnvironmentOutlined,
  UserOutlined, CalendarOutlined, DownOutlined, BankOutlined, RollbackOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';
import { ModalGerarProposta } from './ComercialPipeline';
import ModalNovaComposicao from './ModalNovaComposicao';
import { ViewerProposta } from './ComercialPropostas';
import { EditorComposicaoInline } from './ComercialComposicoes';
import MapaNegocio from '../components/MapaNegocio';

import type { Negocio, AbaKey } from './negocio-detalhe/types';
import { STATUS_LABELS, fmt, fmtDate } from './negocio-detalhe/utils';
import { AbaTimeline } from './negocio-detalhe/AbaTimeline';
import { AbaPropostas } from './negocio-detalhe/AbaPropostas';
import { AbaComposicoes } from './negocio-detalhe/AbaComposicoes';
import { AbaVendas } from './negocio-detalhe/AbaVendas';
import { AbaAtividades } from './negocio-detalhe/AbaAtividades';
import { AbaAnexos } from './negocio-detalhe/AbaAnexos';
import { AbaCreditos } from './negocio-detalhe/AbaCreditos';
import { AbaEdit } from './negocio-detalhe/AbaEdit';
import { ModalGerarVenda } from './negocio-detalhe/ModalGerarVenda';
import { ModalEditarCliente } from './negocio-detalhe/ModalEditarCliente';
import ModalTrocarCliente from './negocio-detalhe/ModalTrocarCliente';
import { ModalEditarContato } from './negocio-detalhe/ModalEditarContato';
// 01/10/2026 — padrão dos CRMs de referência dentro do negócio: trilha de etapas
// com o tempo na etapa, bloco de próximo passo e motivo ao perder.
import { TrilhaEtapas } from './negocio-detalhe/TrilhaEtapas';
import { ProximoPassoBloco } from './negocio-detalhe/ProximoPassoBloco';
import { FollowupBloco } from './negocio-detalhe/FollowupBloco';
import { ModalPerder } from './negocio-detalhe/ModalPerder';
import { usePerms } from '../hooks/usePerms';
import {
  desdeQuandoNaEtapa, diasDesdeData, diasSemMovimento, ehAberto, nivelParado, valorPonderado,
  COR_DO_NIVEL, textoDeDias,
} from './comercial-pipeline/saudeNegocio';

export default function NegocioDetalhe(props: { negocioId: number; onVoltar: () => void; autoGanharTick?: number }) {
  return (
    <AntApp>
      <NegocioDetalheInner {...props} />
    </AntApp>
  );
}

// Cor do Tag de status por etapa. Usa palette Ant pra consistencia com resto do app.
function statusColor(s: string): string {
  switch (s) {
    case 'prospeccao':   return 'default';
    case 'qualificacao': return 'cyan';
    case 'proposta':     return 'blue';
    case 'follow_up':    return 'gold';
    case 'negociacao':   return 'purple';
    case 'ganho':        return 'success';
    case 'perdido':      return 'error';
    case 'cancelado':    return 'default';
    default:             return 'default';
  }
}

// Subsecao dentro do Card unificado da sidebar. Titulo em uppercase pequeno
// (estilo "OUTRAS INFORMACOES" do legado), opcional botao de editar a direita,
// border-bottom entre secoes (exceto a ultima).
function SecaoSidebar({ titulo, icone, children, onEditar, editarHint, onTrocar, trocarHint, ultima }: {
  titulo: string;
  icone?: React.ReactNode;
  children: React.ReactNode;
  onEditar?: () => void;
  editarHint?: string;
  onTrocar?: () => void;
  trocarHint?: string;
  ultima?: boolean;
}) {
  return (
    <div style={{
      padding: 12,
      borderBottom: ultima ? 'none' : '1px solid #f0f0f0',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 8,
      }}>
        <Typography.Text strong style={{
          fontSize: 11, color: '#888', letterSpacing: '0.06em', textTransform: 'uppercase',
        }}>
          <Space size={6}>{icone}{titulo}</Space>
        </Typography.Text>
        <Space size={0}>
          {onTrocar && (
            <Tooltip title={trocarHint || 'Trocar'}>
              <Button type="text" size="small" icon={<SwapOutlined />} onClick={onTrocar} />
            </Tooltip>
          )}
          {onEditar && (
            <Tooltip title={editarHint || 'Editar'}>
              <Button type="text" size="small" icon={<EditOutlined />} onClick={onEditar} />
            </Tooltip>
          )}
        </Space>
      </div>
      {children}
    </div>
  );
}

// Texto generico "Importado da planilha Ploomes em YYYY-MM-DD" é lixo de
// migração — esconde o card de observacoes nesse caso.
function obsRelevante(obs: string | null | undefined): boolean {
  if (!obs) return false;
  const t = obs.trim();
  if (!t) return false;
  if (/^Importado da planilha Ploomes em \d{4}-\d{2}-\d{2}\.?$/i.test(t)) return false;
  return true;
}

function NegocioDetalheInner({ negocioId, onVoltar, autoGanharTick }: {
  negocioId: number;
  onVoltar: () => void;
  autoGanharTick?: number;
}) {
  const { message } = AntApp.useApp();
  // O servidor recusa editar contato sem a permissão (05/10/2026); o lápis some
  // para quem não tem. Falha ao carregar a matriz não vira "não pode".
  const { pode, erro: permsErro } = usePerms();
  const podeEditarContato = permsErro || pode('comercial.contatos.editar');
  // Idem a empresa (05/10/2026): sem `comercial.empresas.editar` o lápis some.
  const podeEditarEmpresa = permsErro || pode('comercial.empresas.editar');
  // Editar o negócio também tem portão desde 05/10/2026 (handlers/negocioPortao.js):
  // etapa, dados (aba Editar, trocar empresa, motivo) e a venda — a data da venda e,
  // desde 06/10/2026, "Ganhar"/"Gerar venda", "Desmarcar Ganho", o contrato do
  // cliente e os créditos de comissão.
  const podeEtapa = permsErro || pode('comercial.pipeline.editar_etapa_funil');
  const podeDadosNegocio = permsErro || pode('comercial.negocio_privado.editar_dados');
  const podeVenda = permsErro || pode('comercial.negocio_privado.gerar_venda');
  const [neg, setNeg] = useState<Negocio | null>(null);
  const [loading, setLoading] = useState(true);
  const [aba, setAba] = useState<AbaKey>('timeline');
  // incrementa ao clicar "Desmarcar Ganho" no topo → AbaVendas abre o modal
  const [desmarcarTick, setDesmarcarTick] = useState(0);
  const [modalGerarProposta, setModalGerarProposta] = useState(false);
  const [modalNovaComposicao, setModalNovaComposicao] = useState(false);
  const [composicaoAbertaId, setComposicaoAbertaId] = useState<string | null>(null);
  const [modalGerarVenda, setModalGerarVenda] = useState(false);
  const [docAberto, setDocAberto] = useState<string | null>(null);
  const [vendedores, setVendedores] = useState<{ email: string; nome: string; telefone?: string }[]>([]);
  const [reloadFlag, setReloadFlag] = useState(0);
  const [editandoCliente, setEditandoCliente] = useState(false);
  const [trocandoCliente, setTrocandoCliente] = useState(false);
  const [editandoContato, setEditandoContato] = useState(false);
  const [perdendo, setPerdendo] = useState(false);
  // Histórico do negócio, lido só para dizer HÁ QUANTO TEMPO ele está na etapa
  // atual (a aba Linha do tempo tem a sua própria leitura). null = ainda não
  // sei — e aí a tela não inventa "há 0 dias".
  const [eventosEtapa, setEventosEtapa] = useState<any[] | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${API_URL}/comercial/vendedores`, { headers: { Authorization: token } });
        const data = res.data?.body ? JSON.parse(res.data.body) : res.data;
        if (Array.isArray(data)) setVendedores(data);
      } catch {}
    })();
  }, []);

  useEffect(() => { carregar(true); }, [negocioId]);

  // Auto-refresh: aba muda OU janela ganha foco → recarrega silenciosamente.
  // setLoading(true) desmontaria filhos (ex MapaNegocio), entao reload silencioso.
  useEffect(() => { setReloadFlag(f => f + 1); }, [aba]);
  useEffect(() => {
    let ultimoFoco = Date.now();
    function aoVoltarFoco() {
      const agora = Date.now();
      if (agora - ultimoFoco > 10000) {
        setReloadFlag(f => f + 1);
        carregar(false);
      }
      ultimoFoco = agora;
    }
    window.addEventListener('focus', aoVoltarFoco);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) aoVoltarFoco();
    });
    return () => {
      window.removeEventListener('focus', aoVoltarFoco);
      document.removeEventListener('visibilitychange', aoVoltarFoco);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function carregar(mostrarLoading = true) {
    if (mostrarLoading) setLoading(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/comercial/propostas`, { headers: { Authorization: token } });
      const data = res.data?.body ? JSON.parse(res.data.body) : res.data;
      const found = data.find((p: any) => p.id === negocioId);
      setNeg(found || null);
      carregarEventosEtapa();
    } finally { if (mostrarLoading) setLoading(false); }
  }

  async function carregarEventosEtapa() {
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/comercial/propostas/${negocioId}/eventos`,
        { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      setEventosEtapa(Array.isArray(d) ? d : []);
    } catch { setEventosEtapa(null); }
  }

  async function mudarStatus(novo: string, extra: Record<string, any> = {}) {
    if (!neg) return;
    // Perder pergunta o motivo antes de gravar (ModalPerder).
    if (novo === 'perdido' && !('motivo_perda' in extra)) { setPerdendo(true); return; }
    try {
      const token = await getToken();
      await axios.put(`${API_URL}/comercial/propostas/${negocioId}`,
        { status: novo, ...extra }, { headers: { Authorization: token } });
      await carregar(false);
      setReloadFlag(f => f + 1);
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
  }

  function onGanhar() {
    if (!neg || !podeVenda) return;
    if (neg.status === 'ganho') {
      if (!window.confirm('Negócio já está marcado como Ganho. Gerar documento de venda mesmo assim? (A data da venda NÃO será alterada.)')) return;
    }
    setModalGerarVenda(true);
  }

  // Atalho do kanban: arrastar o card pra "Fechamento" abre o negócio já
  // disparando o modal "Ganhar" (gera a venda). O pai (ComercialPipeline)
  // incrementa autoGanharTick; aqui abrimos o modal assim que o negócio carrega.
  // A venda só é criada de fato ao confirmar no modal — proteção VND-FIX mantida.
  // O ref garante 1 abertura por tick (não reabre em reloads posteriores).
  const ultimoGanharTick = useRef(0);
  useEffect(() => {
    if (autoGanharTick && autoGanharTick !== ultimoGanharTick.current && podeVenda
        && !loading && neg && !['ganho', 'perdido', 'cancelado'].includes(neg.status)) {
      ultimoGanharTick.current = autoGanharTick;
      setModalGerarVenda(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGanharTick, loading]);

  // VND-FIX (2026-06-09): data da venda editável manualmente (com auditoria
  // no backend via evento data_venda_alterada).
  async function salvarDataVenda(novaData: string) {
    if (!neg) return;
    try {
      const token = await getToken();
      await axios.put(`${API_URL}/comercial/propostas/${negocioId}`,
        { data_fechamento: novaData }, { headers: { Authorization: token } });
      message.success('Data da venda atualizada');
      await carregar(false);
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  async function abrirPdfPloomes(tipo: 'quote' | 'order') {
    try {
      const token = await getToken();
      const r = await axios.get(
        `${API_URL}/comercial/propostas/${negocioId}/doc?tipo=${tipo}`,
        { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      if (d?.url) window.open(d.url, '_blank', 'noopener');
      else message.error('Resposta sem URL');
    } catch (e: any) {
      message.error(e.response?.status === 404
        ? 'PDF não está disponível no S3. Rode "Sincronizar Ploomes" novamente.'
        : `Erro: ${e.response?.data?.error || e.message}`);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 80, textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 12, color: '#888' }}>Carregando negócio…</div>
      </div>
    );
  }
  if (!neg) {
    return (
      <Result status="404" title="Negócio não encontrado"
        extra={<Button type="primary" onClick={onVoltar}>Voltar ao Pipeline</Button>} />
    );
  }

  const isPerdido = neg.status === 'perdido';
  const isCancelado = neg.status === 'cancelado';
  const aberto = ehAberto(neg.status);
  // O gêmeo da licitação muda de etapa pela licitação, nunca à mão (o servidor
  // responde 409) — docs/licitacoes-pipeline.md.
  const ehGemeo = (neg as any).origem === 'licitacao';
  const etapaTravada = ehGemeo
    ? 'A etapa acompanha a licitação — mova em Comercial › Licitações › Pipeline'
    : !podeEtapa ? 'Sem permissão para mudar a etapa (comercial.pipeline.editar_etapa_funil)' : '';
  const desdeEtapa = desdeQuandoNaEtapa(neg.status, eventosEtapa || [], neg.criado_em || neg.data_abertura);
  const diasNaEtapa = eventosEtapa === null ? null : diasDesdeData(desdeEtapa);
  const diasParado = diasSemMovimento(neg as any);
  const idade = diasDesdeData(neg.criado_em || neg.data_abertura);

  // Items do Dropdown de PDFs Ploomes — so aparece se tem deal_id ou order_id.
  const temPloomes = !!((neg as any).ploomes_deal_id || (neg as any).ploomes_order_id);
  const temQuotePdf = !!(neg as any).ploomes_quote_s3_key;
  const temOrderPdf = !!(neg as any).ploomes_order_s3_key;
  const itemsPdfPloomes = [
    {
      key: 'quote',
      label: 'Proposta (PDF)',
      icon: <FilePdfOutlined />,
      disabled: !temQuotePdf,
      onClick: () => temQuotePdf && abrirPdfPloomes('quote'),
    },
    {
      key: 'order',
      label: 'Venda (PDF)',
      icon: <FilePdfOutlined />,
      disabled: !temOrderPdf,
      onClick: () => temOrderPdf && abrirPdfPloomes('order'),
    },
  ];

  return (
    <div style={{ margin: '12px auto', padding: '0 20px' }}>
      {/* ─── HEADER ─── */}
      <div style={{ marginBottom: 14 }}>
        <Space size={12} style={{ marginBottom: 8 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={onVoltar}>Voltar ao Pipeline</Button>
        </Space>
        <Row gutter={[16, 12]} align="middle" justify="space-between" wrap>
          <Col flex="auto" style={{ minWidth: 0 }}>
            <Typography.Title level={4} style={{ margin: 0, color: '#020D7F' }}
              ellipsis={{ tooltip: neg.titulo }}>
              {neg.titulo}
            </Typography.Title>
            <Space size={14} wrap style={{ marginTop: 4 }}>
              {neg.cliente_nome && (
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  <BankOutlined /> {neg.cliente_nome}
                </Typography.Text>
              )}
              <Tag color={statusColor(neg.status)} style={{ marginRight: 0 }}>
                {STATUS_LABELS[neg.status] || neg.status}
              </Tag>
              {/* VND-FIX: data da venda visível e editável quando ganho */}
              {neg.status === 'ganho' && (
                <Space size={4}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>Vendido em:</Typography.Text>
                  <DatePicker size="small" format="DD/MM/YYYY" allowClear={false}
                    disabled={!podeVenda}
                    title={podeVenda ? undefined : 'Corrigir a data da venda exige comercial.negocio_privado.gerar_venda'}
                    value={(neg as any).data_fechamento ? dayjs((neg as any).data_fechamento.slice(0, 10)) : null}
                    onChange={(v) => v && salvarDataVenda(v.format('YYYY-MM-DD'))}
                    style={{ width: 130 }} />
                </Space>
              )}
              {(neg.valor_estimado != null) && (
                <Typography.Text strong style={{ color: '#020D7F', fontSize: 13 }}>
                  {fmt(neg.valor_estimado)}
                </Typography.Text>
              )}
              {(neg.probabilidade != null) && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {neg.probabilidade}% prob.
                </Typography.Text>
              )}
            </Space>
          </Col>
          <Col flex="none">
            <Space size={8} wrap>
              {temPloomes && (
                <Dropdown menu={{ items: itemsPdfPloomes }} placement="bottomRight">
                  <Button icon={<FilePdfOutlined />}>
                    PDFs Ploomes <DownOutlined />
                  </Button>
                </Dropdown>
              )}
              {/* L18 (2026-06-03): botao funciona em qualquer status.
                  Texto adapta: se ja eh ganho, vira "Gerar venda" (sem
                  mover de status); caso contrario, fluxo normal de Ganhar.
                  No gêmeo da licitação é sempre "Gerar venda": a etapa
                  acompanha a licitação (o servidor responde 409). */}
              {podeVenda && (
                <Button type="primary" icon={<CheckOutlined />} onClick={onGanhar}
                  style={{ background: '#0a7f4a', borderColor: '#0a7f4a' }}>
                  {neg.status === 'ganho' || ehGemeo ? 'Gerar venda' : 'Ganhar'}
                </Button>
              )}
              {/* L18: botao Desmarcar Ganho visivel no topo quando aplicavel */}
              {neg.status === 'ganho' && podeVenda && !ehGemeo && (
                <Button icon={<RollbackOutlined />}
                  onClick={() => { setAba('vendas'); setDesmarcarTick(t => t + 1); }}
                  title="Reverte o negócio ganho — abre a confirmação na aba Vendas.">
                  Desmarcar Ganho
                </Button>
              )}
              <Button danger icon={<CloseOutlined />}
                onClick={() => mudarStatus('perdido')}
                disabled={neg.status === 'perdido' || neg.status === 'ganho' || !!etapaTravada}
                title={neg.status === 'ganho' ? 'Negócio ganho — use "Desmarcar Ganho" antes se precisar reverter'
                  : etapaTravada || undefined}>
                Perder
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* ─── TRILHA DE ETAPAS + INDICADORES ─── */}
      <Card size="small" style={{ marginBottom: 14 }} styles={{ body: { padding: 12 } }}>
        {/* Negócio fechado não mostra "há N dias" na trilha: sem histórico de etapa a
            conta parte da criação do registro — que, nos negócios importados, é a
            data da importação, não a do fechamento. Para eles vale "Fechado em". */}
        <TrilhaEtapas status={neg.status} diasNaEtapa={aberto ? diasNaEtapa : null}
          onMudar={etapaTravada ? undefined : (s) => mudarStatus(s)}
          onGanhar={podeVenda && !ehGemeo ? onGanhar : undefined}
          travadaPorque={etapaTravada || undefined} />
        {(isPerdido || isCancelado) && (
          <Typography.Text style={{ display: 'block', marginTop: 8, fontSize: 12.5 }}>
            <Tag color="error" style={{ marginRight: 8 }}>{STATUS_LABELS[neg.status]}</Tag>
            {isPerdido && (
              <>Motivo: <b>{neg.motivo_perda || 'não informado'}</b>
                {!neg.motivo_perda && (podeDadosNegocio || podeEtapa) && (
                  <Button type="link" size="small" onClick={() => setPerdendo(true)}>informar o motivo</Button>
                )}</>
            )}
          </Typography.Text>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 28px', marginTop: 12 }}>
          {[
            { rotulo: 'Valor', valor: fmt(neg.status === 'ganho' && Number(neg.valor_final) > 0 ? neg.valor_final : neg.valor_estimado) },
            ...(aberto ? [{ rotulo: `Ponderado (${neg.probabilidade ?? 0}%)`, valor: fmt(valorPonderado(neg as any)) }] : []),
            ...(aberto && idade != null ? [{ rotulo: 'Idade do negócio', valor: idade === 0 ? 'hoje' : `${idade} dias` }] : []),
            ...(!aberto && neg.data_fechamento ? [{ rotulo: 'Fechado em', valor: fmtDate(neg.data_fechamento) }] : []),
            ...(aberto && diasNaEtapa != null ? [{ rotulo: 'Na etapa atual', valor: diasNaEtapa === 0 ? 'desde hoje' : textoDeDias(diasNaEtapa) }] : []),
            ...(aberto && diasParado != null ? [{
              rotulo: 'Último movimento', valor: textoDeDias(diasParado), cor: COR_DO_NIVEL[nivelParado(neg as any)],
            }] : []),
          ].map((m: any) => (
            <div key={m.rotulo} style={{ minWidth: 0 }}>
              <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: '#888' }}>
                {m.rotulo}
              </div>
              <div style={{ fontSize: 15, fontWeight: 600, color: m.cor || '#020D7F', fontVariantNumeric: 'tabular-nums' }}>
                {m.valor}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* ─── CORPO: sidebar 320 + conteudo direito ─── */}
      <Row gutter={16} align="top">
        <Col xs={24} md={8} lg={7} xl={6} style={{ minWidth: 0 }}>
          {/* Sidebar virou 1 Card unico com secoes separadas — antes eram N
              Cards e o topo dele desalinhava do Card direito (Tabs sem head). */}
          <Card size="small" styles={{ body: { padding: 0 } }}>
            {/* Empresa — so se tem cliente vinculado */}
            {neg.cliente_id && neg.cliente_nome && (
              <SecaoSidebar
                titulo="Empresa" icone={<BankOutlined />}
                onTrocar={podeDadosNegocio ? () => setTrocandoCliente(true) : undefined}
                trocarHint="Trocar empresa (vincular outra)"
                onEditar={podeEditarEmpresa ? () => setEditandoCliente(true) : undefined}
                editarHint="Editar empresa (atualiza base)">
                <Descriptions size="small" column={1} colon={false}
                  labelStyle={{ color: '#888', fontSize: 11, width: 80 }}
                  contentStyle={{ fontSize: 12 }}>
                  <Descriptions.Item label="Razão">
                    {neg.cliente_nome}
                  </Descriptions.Item>
                  {(neg as any).cnpj && (
                    <Descriptions.Item label="CNPJ">{(neg as any).cnpj}</Descriptions.Item>
                  )}
                  {(neg as any).cliente_cidade && (
                    <Descriptions.Item label="Cidade">
                      <EnvironmentOutlined /> {(neg as any).cliente_cidade}
                      {(neg as any).cliente_uf ? `/${(neg as any).cliente_uf}` : ''}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </SecaoSidebar>
            )}

            {/* Contato — so se tem contato vinculado */}
            {neg.contato_id && neg.contato_nome && (
              <SecaoSidebar
                titulo="Contato" icone={<UserOutlined />}
                onEditar={podeEditarContato ? () => setEditandoContato(true) : undefined}
                editarHint="Editar contato (atualiza base)">
                <Descriptions size="small" column={1} colon={false}
                  labelStyle={{ color: '#888', fontSize: 11, width: 60 }}
                  contentStyle={{ fontSize: 12 }}>
                  <Descriptions.Item label="Nome">{neg.contato_nome}</Descriptions.Item>
                  {neg.contato_email && (
                    <Descriptions.Item label="E-mail">
                      <a href={`mailto:${neg.contato_email}`}>
                        <MailOutlined /> {neg.contato_email}
                      </a>
                    </Descriptions.Item>
                  )}
                  {(neg as any).contato_telefone && (
                    <Descriptions.Item label="Tel">
                      <PhoneOutlined /> {(neg as any).contato_telefone}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </SecaoSidebar>
            )}

            {/* Datas — só se tem alguma data significativa */}
            {(neg.data_abertura || neg.data_envio || neg.data_fechamento) && (
              <SecaoSidebar titulo="Datas" icone={<CalendarOutlined />}>
                <Descriptions size="small" column={1} colon={false}
                  labelStyle={{ color: '#888', fontSize: 11, width: 80 }}
                  contentStyle={{ fontSize: 12 }}>
                  {neg.data_abertura && (
                    <Descriptions.Item label="Abertura">{fmtDate(neg.data_abertura)}</Descriptions.Item>
                  )}
                  {neg.data_envio && (
                    <Descriptions.Item label="Envio">{fmtDate(neg.data_envio)}</Descriptions.Item>
                  )}
                  {neg.data_fechamento && (
                    <Descriptions.Item label="Fechamento">{fmtDate(neg.data_fechamento)}</Descriptions.Item>
                  )}
                </Descriptions>
              </SecaoSidebar>
            )}

            {/* Responsavel — so se setado */}
            {neg.responsavel && (
              <SecaoSidebar titulo="Responsável" icone={<UserOutlined />}>
                <Tag color="blue">{neg.responsavel}</Tag>
              </SecaoSidebar>
            )}

            {/* Valor estimado / area — destacam num so card */}
            <SecaoSidebar titulo="Valores">
              <Row gutter={8}>
                <Col span={12}>
                  <Statistic title="Estimado" value={Number(neg.valor_estimado) || 0}
                    formatter={(v) => fmt(v)}
                    valueStyle={{ color: '#020D7F', fontSize: 16, fontWeight: 700 }} />
                </Col>
                <Col span={12}>
                  <Statistic title="Probabilidade" value={neg.probabilidade ?? 0} suffix="%"
                    valueStyle={{ color: '#0930D0', fontSize: 16, fontWeight: 700 }} />
                </Col>
              </Row>
              {(neg as any).area && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>Área</Typography.Text>
                  <div><Tag>{(neg as any).area}</Tag></div>
                </div>
              )}
            </SecaoSidebar>

            {/* Observacoes — esconde se vazio ou só "Importado da planilha…" */}
            {obsRelevante(neg.observacoes) && (
              <SecaoSidebar titulo="Observações" ultima>
                <Typography.Paragraph style={{ fontSize: 12, whiteSpace: 'pre-wrap', margin: 0 }}>
                  {neg.observacoes}
                </Typography.Paragraph>
              </SecaoSidebar>
            )}
          </Card>
        </Col>

        {/* ─── DIREITA: tabs ─── */}
        <Col xs={24} md={16} lg={17} xl={18} style={{ minWidth: 0 }}>
          <ProximoPassoBloco negocio={neg as any} reloadFlag={reloadFlag}
            onMudou={() => setReloadFlag(f => f + 1)} />
          <FollowupBloco negocio={neg as any} reloadFlag={reloadFlag}
            onMudou={() => setReloadFlag(f => f + 1)} />
          <Card size="small" bodyStyle={{ padding: 0 }}>
            <Tabs activeKey={aba} onChange={(k) => setAba(k as AbaKey)} size="small"
              style={{ padding: '0 12px' }}
              items={[
                { key: 'timeline',    label: 'Linha do tempo' },
                { key: 'propostas',   label: 'Propostas' },
                { key: 'composicoes', label: 'Composições' },
                { key: 'vendas',      label: 'Vendas' },
                { key: 'atividades',  label: 'Atividades' },
                { key: 'creditos',    label: 'Créditos' },
                { key: 'anexos',      label: 'Anexos' },
                { key: 'mapa',        label: 'Mapa' },
                { key: 'edit',        label: 'Editar dados' },
              ]}
            />
            <div style={{ padding: 16 }}>
              {aba === 'timeline'    && <AbaTimeline negocioId={negocioId} negocio={neg} reloadFlag={reloadFlag} />}
              {aba === 'propostas'   && <AbaPropostas negocioId={negocioId}
                onGerarNova={() => setModalGerarProposta(true)}
                onAbrirDoc={(id) => setDocAberto(id)} reloadFlag={reloadFlag} />}
              {aba === 'composicoes' && <AbaComposicoes negocioId={negocioId}
                onNova={() => setModalNovaComposicao(true)}
                onAbrir={(id) => setComposicaoAbertaId(id)} reloadFlag={reloadFlag} />}
              {aba === 'vendas'      && <AbaVendas negocioId={negocioId} negocio={neg}
                onAbrirDoc={(id) => setDocAberto(id)} reloadFlag={reloadFlag}
                autoDesmarcarTick={desmarcarTick}
                onNegocioChanged={async () => { await carregar(false); setReloadFlag(f => f + 1); }} />}
              {aba === 'atividades'  && <AbaAtividades negocioId={negocioId} negocio={neg} reloadFlag={reloadFlag} />}
              {aba === 'creditos'    && <AbaCreditos negocioId={negocioId} reloadFlag={reloadFlag} />}
              {aba === 'anexos'      && <AbaAnexos negocioId={negocioId} reloadFlag={reloadFlag} />}
              {aba === 'mapa'        && <MapaNegocio negocioId={negocioId} />}
              {aba === 'edit'        && <AbaEdit neg={neg} vendedores={vendedores} onSaved={carregar} />}
            </div>
          </Card>
        </Col>
      </Row>

      {/* ─── MODAIS IN-PLACE ─── */}
      {modalGerarProposta && (
        <ModalGerarProposta
          propostaId={negocioId}
          tituloNegocio={neg.titulo}
          responsavel={neg.responsavel}
          vendedores={vendedores}
          onClose={() => setModalGerarProposta(false)}
          onGerada={async () => {
            setModalGerarProposta(false);
            setReloadFlag(f => f + 1);
            setAba('propostas');
            await carregar();
          }}
        />
      )}
      {modalNovaComposicao && (
        <ModalNovaComposicao
          propostaId={negocioId}
          tituloNegocio={neg.titulo}
          onClose={() => setModalNovaComposicao(false)}
          onCriada={(composicaoId) => {
            setModalNovaComposicao(false);
            setReloadFlag(f => f + 1);
            setComposicaoAbertaId(composicaoId);
          }}
        />
      )}
      {composicaoAbertaId && (
        <EditorComposicaoInline
          composicaoId={composicaoAbertaId}
          onClose={() => setComposicaoAbertaId(null)}
          onSaved={() => {
            setComposicaoAbertaId(null);
            setReloadFlag(f => f + 1);
          }}
        />
      )}
      {modalGerarVenda && (
        <ModalGerarVenda
          propostaId={negocioId}
          tituloNegocio={neg.titulo}
          statusAtual={neg.status}
          gemeo={ehGemeo}
          onClose={() => setModalGerarVenda(false)}
          onGerada={async () => {
            // L18: status agora eh decidido pelo backend baseado em marcar_ganho
            // (checkbox no modal). Aqui so recarrega — nao força mudança de status.
            setModalGerarVenda(false);
            setReloadFlag(f => f + 1);
            setAba('vendas');
          }}
        />
      )}
      {docAberto && (
        <ViewerProposta
          id={docAberto}
          onClose={() => setDocAberto(null)}
          onSaved={async () => { setReloadFlag(f => f + 1); }}
        />
      )}
      {editandoCliente && neg.cliente_id && (
        <ModalEditarCliente
          clienteId={neg.cliente_id}
          onClose={() => setEditandoCliente(false)}
          onSaved={async () => { setEditandoCliente(false); await carregar(); }}
        />
      )}
      {trocandoCliente && neg.cliente_id && (
        <ModalTrocarCliente
          propostaId={neg.id}
          clienteAtualId={neg.cliente_id}
          clienteAtualNome={neg.cliente_nome || ''}
          onClose={() => setTrocandoCliente(false)}
          onSaved={async () => { setTrocandoCliente(false); await carregar(); }}
        />
      )}
      {perdendo && (
        <ModalPerder titulo={neg.titulo} onCancelar={() => setPerdendo(false)}
          onConfirmar={async (motivo) => {
            // Negócio que JÁ está perdido só ganha o motivo; o resto muda de etapa.
            if (neg.status === 'perdido') {
              try {
                const token = await getToken();
                await axios.put(`${API_URL}/comercial/propostas/${negocioId}`,
                  { motivo_perda: motivo }, { headers: { Authorization: token } });
                await carregar(false);
              } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
            } else {
              await mudarStatus('perdido', { motivo_perda: motivo });
            }
            setPerdendo(false);
          }} />
      )}
      {editandoContato && neg.contato_id && neg.cliente_id && (
        <ModalEditarContato
          contatoId={neg.contato_id}
          clienteId={neg.cliente_id}
          onClose={() => setEditandoContato(false)}
          onSaved={async () => { setEditandoContato(false); await carregar(); }}
        />
      )}
    </div>
  );
}
